import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiX, FiUser, FiPhone, FiGlobe, FiCheckCircle, FiMapPin, FiMap, FiAlertCircle, FiPlus, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BaseUrl from '../../../Url/BaseUrl';

const EditOperator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { operatorNumber } = useParams();
  
  const [masterData, setMasterData] = useState({
    states: [],
    zones: []
  });
  
  const [formData, setFormData] = useState({
    operatorNumber: '',
    operatorName: '',
    operatorNameHi: '',
    status: true,
    mobileNumbers: [{ dialCode: '91', number: '' }],
    appLink: '',
    iOSAppLink: '',
    websiteContactLink: '',
    stateCode: '',
    state: null,
    zoneCode: '',
    zone: null
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingMasterData, setLoadingMasterData] = useState({
    states: false,
    zones: false
  });

  // List of supported dial codes (must match the dropdown options)
  const supportedDialCodes = ['91', '1', '44', '61', '971', '65', '60', '64', '49', '33', '81', '86', '82', '966', '974', '965', '968', '880', '94', '977', '92', '93'];

  // CORRECTED PARSER: extracts dial code by matching known country codes
  const parseContactNumbers = (apiNumbers) => {
    if (!apiNumbers) return [{ dialCode: '91', number: '' }];
    
    let numbersArray = [];
    if (Array.isArray(apiNumbers)) {
      numbersArray = apiNumbers;
    } else if (typeof apiNumbers === 'string') {
      numbersArray = apiNumbers.includes(',') 
        ? apiNumbers.split(',').map(num => num.trim())
        : [apiNumbers];
    }
    
    if (numbersArray.length === 0) {
      return [{ dialCode: '91', number: '' }];
    }
    
    const parsed = [];
    
    for (const raw of numbersArray) {
      let numberStr = raw.trim();
      if (!numberStr) continue;
      
      let dialCode = '91';
      let numberPart = numberStr;
      
      // Extract dial code if number starts with '+'
      if (numberStr.startsWith('+')) {
        const withoutPlus = numberStr.substring(1);
        
        // Try to match the longest known dial code at the beginning
        let matchedCode = null;
        for (const code of supportedDialCodes) {
          if (withoutPlus.startsWith(code)) {
            if (!matchedCode || code.length > matchedCode.length) {
              matchedCode = code;
            }
          }
        }
        
        if (matchedCode) {
          dialCode = matchedCode;
          numberPart = withoutPlus.substring(matchedCode.length);
        } else {
          // Fallback: assume first 2 digits are dial code (for India)
          dialCode = withoutPlus.substring(0, 2);
          numberPart = withoutPlus.substring(2);
        }
      }
      
      // Keep only digits
      numberPart = numberPart.replace(/\D/g, '');
      if (numberPart.length === 0) continue;
      
      parsed.push({ dialCode, number: numberPart });
    }
    
    // Remove duplicates
    const unique = [];
    const seen = new Set();
    for (const item of parsed) {
      const key = `${item.dialCode}|${item.number}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(item);
      }
    }
    
    if (unique.length === 0) {
      return [{ dialCode: '91', number: '' }];
    }
    return unique;
  };

  // Fetch operator data
  const fetchOperatorData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      const response = await axios.get(
        `${BaseUrl.baseurl}BusOperators/${operatorNumber}`,
        {
          headers: { 'accept': 'text/plain' }
        }
      );
      
      const operatorData = response.data;
      
      // Use the corrected parser
      let mobileNumbersArray = parseContactNumbers(operatorData.busOperatorContactNumber);
      
      setFormData({
        operatorNumber: operatorData.operatorNumber || '',
        operatorName: operatorData.operatorName || '',
        operatorNameHi: operatorData.hindi || '',
        status: operatorData.status || false,
        mobileNumbers: mobileNumbersArray,
        appLink: operatorData.appLink || '',
        iOSAppLink: operatorData.iOSAppLink || '',
        websiteContactLink: operatorData.websiteContactLink || '',
        stateCode: operatorData.stateCode || '',
        state: operatorData.state || null,
        zoneCode: operatorData.zoneCode || '',
        zone: operatorData.zone || null
      });
      
    } catch (error) {
      console.error('Error fetching operator data:', error);
      const errorMsg = error.response?.data?.message || error.response?.status 
        ? t('serverResponseError', { status: error.response.status }) 
        : error.message;
      setErrorMessage(`${t('failedToLoadData')}: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch states
  const fetchStates = async () => {
    try {
      setLoadingMasterData(prev => ({ ...prev, states: true }));
      
      const response = await axios.get(`${BaseUrl.baseurl}States`, {
        headers: { 'accept': 'text/plain' }
      });
      
      setMasterData(prev => ({ ...prev, states: response.data }));
      
    } catch (error) {
      console.error('Error fetching states:', error);
      setErrorMessage(t('failedToLoadStates'));
      setMasterData(prev => ({ ...prev, states: [] }));
    } finally {
      setLoadingMasterData(prev => ({ ...prev, states: false }));
    }
  };

  // Fetch zones
  const fetchZones = async () => {
    try {
      setLoadingMasterData(prev => ({ ...prev, zones: true }));
      
      const response = await axios.get(`${BaseUrl.baseurl}Zones`, {
        headers: { 'accept': 'text/plain' }
      });
      
      setMasterData(prev => ({ ...prev, zones: response.data }));
      
    } catch (error) {
      console.error('Error fetching zones:', error);
      setErrorMessage(t('failedToLoadZones'));
      setMasterData(prev => ({ ...prev, zones: [] }));
    } finally {
      setLoadingMasterData(prev => ({ ...prev, zones: false }));
    }
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([fetchStates(), fetchZones(), fetchOperatorData()]);
      } catch (error) {
        console.error('Error initializing data:', error);
        setErrorMessage(t('failedToLoadData'));
      }
    };
    
    initializeData();
  }, [operatorNumber]);

  // Filter zones based on selected state
  const [filteredZones, setFilteredZones] = useState([]);
  
  useEffect(() => {
    if (formData.stateCode && masterData.zones.length > 0) {
      const zonesForState = masterData.zones.filter(zone => zone.stateCode === formData.stateCode);
      setFilteredZones(zonesForState);
      
      if (formData.zoneCode) {
        const currentZone = masterData.zones.find(z => z.zoneCode === formData.zoneCode);
        if (currentZone && currentZone.stateCode !== formData.stateCode) {
          setFormData(prev => ({
            ...prev,
            zoneCode: '',
            zone: null
          }));
        }
      }
    } else {
      setFilteredZones(masterData.zones);
    }
  }, [masterData.zones, formData.stateCode, formData.zoneCode]);

  // Mobile number handlers
  const handleMobileNumberChange = (index, field, value) => {
    if (field === 'dialCode') {
      const newMobileNumbers = [...formData.mobileNumbers];
      newMobileNumbers[index] = { ...newMobileNumbers[index], dialCode: value };
      setFormData(prev => ({
        ...prev,
        mobileNumbers: newMobileNumbers
      }));
    } else if (field === 'number') {
      const numericValue = value.replace(/\D/g, '');
      const newMobileNumbers = [...formData.mobileNumbers];
      newMobileNumbers[index] = { ...newMobileNumbers[index], number: numericValue };
      setFormData(prev => ({
        ...prev,
        mobileNumbers: newMobileNumbers
      }));
    }
    
    if (validationErrors[`mobileNumber${index}`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`mobileNumber${index}`];
        return newErrors;
      });
    }
  };

  const addMobileNumberField = () => {
    setFormData(prev => ({
      ...prev,
      mobileNumbers: [...prev.mobileNumbers, { dialCode: '91', number: '' }]
    }));
  };

  const removeMobileNumberField = (index) => {
    if (formData.mobileNumbers.length > 1) {
      const newMobileNumbers = formData.mobileNumbers.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        mobileNumbers: newMobileNumbers
      }));
      
      if (validationErrors[`mobileNumber${index}`]) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`mobileNumber${index}`];
          return newErrors;
        });
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.operatorNumber.trim()) {
      errors.operatorNumber = t('operatorNumberRequired');
    }
    
    if (!formData.operatorName.trim()) {
      errors.operatorName = t('englishNameRequired');
    }
    
    if (!formData.stateCode) {
      errors.stateCode = t('stateRequired');
    }
    
    if (!formData.zoneCode) {
      errors.zoneCode = t('zoneRequired');
    }
    
    formData.mobileNumbers.forEach((mobile, index) => {
      if (mobile.number && mobile.number.length < 8 && mobile.number.length > 0) {
        errors[`mobileNumber${index}`] = t('mobileNumberTooShort', 'Mobile number is too short (minimum 8 digits).');
      }
    });
    
    if (formData.appLink && !/^https?:\/\/.+/.test(formData.appLink)) {
      errors.appLink = t('invalidUrlFormat');
    }
    
    if (formData.iOSAppLink && !/^https?:\/\/.+/.test(formData.iOSAppLink)) {
      errors.iOSAppLink = t('invalidUrlFormat');
    }
    
    if (formData.websiteContactLink && !/^https?:\/\/.+/.test(formData.websiteContactLink)) {
      errors.websiteContactLink = t('invalidUrlFormat');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (name === 'stateCode') {
        const selectedState = masterData.states.find(state => state.stateCode === value);
        updatedData.state = selectedState ? selectedState.stateName : null;
        if (prev.stateCode !== value) {
          updatedData.zoneCode = '';
          updatedData.zone = null;
        }
      }
      
      if (name === 'zoneCode') {
        const selectedZone = masterData.zones.find(zone => zone.zoneCode === value);
        updatedData.zone = selectedZone ? selectedZone.zoneName : null;
      }
      
      return updatedData;
    });
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setErrorMessage('');
      
      try {
        const validMobileNumbers = formData.mobileNumbers
          .filter(mobile => mobile.number && mobile.number.trim() !== '')
          .map(mobile => `+${mobile.dialCode}${mobile.number}`);
        
        const submissionData = {
          operatorNumber: formData.operatorNumber,
          operatorName: formData.operatorName,
          hindi: formData.operatorNameHi || null,
          status: formData.status,
          busOperatorContactNumber: validMobileNumbers.length > 0 ? validMobileNumbers : [],
          appLink: formData.appLink || null,
          iOSAppLink: formData.iOSAppLink || null,
          websiteContactLink: formData.websiteContactLink || null,
          stateCode: formData.stateCode,
          zoneCode: formData.zoneCode
        };
        
        console.log('Submitting operator update:', submissionData);
        
        const response = await axios.put(
          `${BaseUrl.baseurl}BusOperators/${operatorNumber}`,
          submissionData,
          {
            headers: {
              'Content-Type': 'application/json',
              'accept': 'text/plain'
            }
          }
        );
        
        console.log('Operator updated successfully:', response.data);
        
        setSuccessMessage(t('statusUpdated', { status: t('updated') }));
        
        setTimeout(() => {
          navigate('/bus-finder/operators/manage');
        }, 1500);
        
      } catch (error) {
        console.error('Update error:', error);
        const errorMsg = error.response?.data?.message || error.response?.status 
          ? t('serverResponseError', { status: error.response.status }) 
          : error.message;
        setErrorMessage(`${t('registrationFailed')}: ${errorMsg}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    if (window.confirm(t('confirmCancel'))) {
      navigate('/bus-finder/operators/manage');
    }
  };

  const handleRetry = async () => {
    setErrorMessage('');
    setIsLoading(true);
    try {
      await Promise.all([fetchStates(), fetchZones(), fetchOperatorData()]);
    } catch (error) {
      console.error('Error retrying:', error);
      setErrorMessage(t('failedToLoadDataRetry'));
    }
  };

  if (isLoading) {
    return (
      <div className="form-container">
        <div className="form-header">
           <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
      
          <h1><FiUser /> {t('edit')} {t('busOperatorsManagement')}</h1>
          <p>{t('loadingFormData')}</p>
        </div>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
         <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <p>{t('edit')} {t('busOperatorsManagement')}: <strong>{operatorNumber}</strong></p>
      </div>

      {errorMessage && (
        <div className="alert alert-error">
          <FiAlertCircle /> 
          <span>{errorMessage}</span>
          <button 
            type="button" 
            className="btn-retry"
            onClick={handleRetry}
            disabled={isLoading}
          >
            {t('retry')}
          </button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <FiCheckCircle /> {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-section">
          <h3>{t('basicInformation')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="operatorNumber">
                {t('operatorNumberLabel')} *
              </label>
              <input
                type="text"
                id="operatorNumber"
                name="operatorNumber"
                value={formData.operatorNumber}
                onChange={handleChange}
                placeholder={t('operatorNumberPlaceholder')}
                className={validationErrors.operatorNumber ? 'error' : ''}
                disabled={isSubmitting}
                readOnly
              />
              {validationErrors.operatorNumber && (
                <span className="error-text">{validationErrors.operatorNumber}</span>
              )}
              <div className="hint">{t('operatorNumberHint')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="operatorName">
                {t('operatorNameTitle')} (English) *
              </label>
              <input
                type="text"
                id="operatorName"
                name="operatorName"
                value={formData.operatorName}
                onChange={handleChange}
                placeholder={t('englishNamePlaceholder')}
                className={validationErrors.operatorName ? 'error' : ''}
                disabled={isSubmitting}
              />
              {validationErrors.operatorName && (
                <span className="error-text">{validationErrors.operatorName}</span>
              )}
              <div className="hint">{t('englishNameHint')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="operatorNameHi">
                {t('hindiName', 'Hindi Name')}
              </label>
              <input
                type="text"
                id="operatorNameHi"
                name="operatorNameHi"
                value={formData.operatorNameHi}
                onChange={handleChange}
                placeholder={t('hindiNamePlaceholder', 'Enter Hindi name')}
                className={validationErrors.operatorNameHi ? 'error' : ''}
                disabled={isSubmitting}
              />
              {validationErrors.operatorNameHi && (
                <span className="error-text">{validationErrors.operatorNameHi}</span>
              )}
              <div className="hint">{t('hindiNameHint', 'Operator name in Hindi (optional)')}</div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('locationInformation')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="stateCode">
                <FiMapPin /> {t('stateLabel')} *
                {loadingMasterData.states && (
                  <span className="loading-indicator">
                    <div className="spinner-tiny"></div>
                  </span>
                )}
              </label>
              <select
                id="stateCode"
                name="stateCode"
                value={formData.stateCode}
                onChange={handleChange}
                className={validationErrors.stateCode ? 'error' : ''}
                disabled={loadingMasterData.states || isSubmitting}
              >
                <option value="">{t('selectStateOption')}</option>
                {loadingMasterData.states ? (
                  <option value="" disabled>{t('loading')}</option>
                ) : masterData.states.length === 0 ? (
                  <option value="" disabled>{t('failedToLoadStates')}</option>
                ) : (
                  masterData.states.map(state => (
                    <option key={state.stateCode} value={state.stateCode}>
                      {state.stateName} ({state.stateCode})
                    </option>
                  ))
                )}
              </select>
              {validationErrors.stateCode && (
                <span className="error-text">{validationErrors.stateCode}</span>
              )}
              {formData.stateCode && !loadingMasterData.states && (
                <div className="selected-info">
                  {t('selected')}: <strong>
                    {masterData.states.find(s => s.stateCode === formData.stateCode)?.stateName || formData.stateCode}
                  </strong>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="zoneCode">
                <FiMap /> {t('zoneLabel')} *
                {loadingMasterData.zones && (
                  <span className="loading-indicator">
                    <div className="spinner-tiny"></div>
                  </span>
                )}
              </label>
              <select
                id="zoneCode"
                name="zoneCode"
                value={formData.zoneCode}
                onChange={handleChange}
                className={validationErrors.zoneCode ? 'error' : ''}
                disabled={loadingMasterData.zones || isSubmitting || filteredZones.length === 0}
              >
                <option value="">{t('selectZoneOption')}</option>
                {loadingMasterData.zones ? (
                  <option value="" disabled>{t('loading')}</option>
                ) : filteredZones.length === 0 ? (
                  <option value="" disabled>
                    {formData.stateCode ? t('noZonesAvailable') : t('selectStateFirst')}
                  </option>
                ) : (
                  filteredZones.map(zone => (
                    <option key={zone.zoneCode} value={zone.zoneCode}>
                      {zone.zoneName} ({zone.zoneCode})
                    </option>
                  ))
                )}
              </select>
              {validationErrors.zoneCode && (
                <span className="error-text">{validationErrors.zoneCode}</span>
              )}
              {formData.zoneCode && !loadingMasterData.zones && (
                <div className="selected-info">
                  {t('selected')}: <strong>
                    {masterData.zones.find(z => z.zoneCode === formData.zoneCode)?.zoneName || formData.zoneCode}
                  </strong>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('contactInformation')}</h3>
          <div className="mobile-numbers-section">
            {formData.mobileNumbers.map((mobile, index) => (
              <div key={index} className="mobile-number-group">
                <div className="form-group">
                  <label>
                    <FiPhone /> {t('mobileNumber')} {index > 0 ? `#${index + 1}` : ''}
                  </label>
                  <div className="phone-input-wrapper">
                    <select
                      value={mobile.dialCode}
                      onChange={(e) => handleMobileNumberChange(index, 'dialCode', e.target.value)}
                      className="country-code-select"
                      disabled={isSubmitting}
                    >
                      <option value="91">🇮🇳 +91 (India)</option>
                      <option value="1">🇺🇸 +1 (USA/Canada)</option>
                      <option value="44">🇬🇧 +44 (UK)</option>
                      <option value="61">🇦🇺 +61 (Australia)</option>
                      <option value="971">🇦🇪 +971 (UAE)</option>
                      <option value="65">🇸🇬 +65 (Singapore)</option>
                      <option value="60">🇲🇾 +60 (Malaysia)</option>
                      <option value="64">🇳🇿 +64 (New Zealand)</option>
                      <option value="49">🇩🇪 +49 (Germany)</option>
                      <option value="33">🇫🇷 +33 (France)</option>
                      <option value="81">🇯🇵 +81 (Japan)</option>
                      <option value="86">🇨🇳 +86 (China)</option>
                      <option value="82">🇰🇷 +82 (South Korea)</option>
                      <option value="966">🇸🇦 +966 (Saudi Arabia)</option>
                      <option value="974">🇶🇦 +974 (Qatar)</option>
                      <option value="965">🇰🇼 +965 (Kuwait)</option>
                      <option value="968">🇴🇲 +968 (Oman)</option>
                      <option value="880">🇧🇩 +880 (Bangladesh)</option>
                      <option value="94">🇱🇰 +94 (Sri Lanka)</option>
                      <option value="977">🇳🇵 +977 (Nepal)</option>
                      <option value="92">🇵🇰 +92 (Pakistan)</option>
                      <option value="93">🇦🇫 +93 (Afghanistan)</option>
                    </select>
                    <input
                      type="tel"
                      value={mobile.number}
                      onChange={(e) => handleMobileNumberChange(index, 'number', e.target.value)}
                      placeholder={t('contactNumberPlaceholder')}
                      className={validationErrors[`mobileNumber${index}`] ? 'error' : ''}
                      disabled={isSubmitting}
                    />
                    {index > 0 && (
                      <button
                        type="button"
                        className="btn-remove-number"
                        onClick={() => removeMobileNumberField(index)}
                        disabled={isSubmitting}
                        title={t('removeNumber', 'Remove this number')}
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                  {validationErrors[`mobileNumber${index}`] && (
                    <span className="error-text">{validationErrors[`mobileNumber${index}`]}</span>
                  )}
                  <div className="hint">{t('mobileNumberHint', 'Select country code and enter phone number')}</div>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              className="btn-add-number"
              onClick={addMobileNumberField}
              disabled={isSubmitting}
            >
              <FiPlus /> {t('addAnotherMobileNumber', 'Add Another Mobile Number')}
            </button>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('digitalPresence')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="appLink">
                <FiGlobe /> {t('androidAppLink', 'Android App Link')}
              </label>
              <input
                type="url"
                id="appLink"
                name="appLink"
                value={formData.appLink}
                onChange={handleChange}
                placeholder={t('googlePlayStorePlaceholder')}
                className={validationErrors.appLink ? 'error' : ''}
                disabled={isSubmitting}
              />
              {validationErrors.appLink && (
                <span className="error-text">{validationErrors.appLink}</span>
              )}
              <div className="hint">{t('googlePlayStoreLink')} ({t('optional')})</div>
            </div>

            <div className="form-group">
              <label htmlFor="iOSAppLink">
                <FiGlobe /> {t('iosAppLink', 'iOS App Link')}
              </label>
              <input
                type="url"
                id="iOSAppLink"
                name="iOSAppLink"
                value={formData.iOSAppLink}
                onChange={handleChange}
                placeholder={t('appleAppStorePlaceholder')}
                className={validationErrors.iOSAppLink ? 'error' : ''}
                disabled={isSubmitting}
              />
              {validationErrors.iOSAppLink && (
                <span className="error-text">{validationErrors.iOSAppLink}</span>
              )}
              <div className="hint">{t('appleAppStoreLink')} ({t('optional')})</div>
            </div>

            <div className="form-group">
              <label htmlFor="websiteContactLink">
                <FiGlobe /> {t('website')}
              </label>
              <input
                type="url"
                id="websiteContactLink"
                name="websiteContactLink"
                value={formData.websiteContactLink}
                onChange={handleChange}
                placeholder={t('websitePlaceholder', 'https://example.com')}
                className={validationErrors.websiteContactLink ? 'error' : ''}
                disabled={isSubmitting}
              />
              {validationErrors.websiteContactLink && (
                <span className="error-text">{validationErrors.websiteContactLink}</span>
              )}
              <div className="hint">{t('websiteHint')} ({t('optional')})</div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('statusSettings')}</h3>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="status"
                checked={formData.status}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">
                {formData.status ? t('active') : t('inactive')} {t('status')}
              </span>
            </label>
            <p className="hint">
              {formData.status 
                ? t('activeStatusHint') 
                : t('inactiveOperatorsHint')}
            </p>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <FiX /> {t('cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || loadingMasterData.states || loadingMasterData.zones}
          >
            {isSubmitting ? (
              <>
                <div className="spinner-small"></div>
                {t('registering')}
              </>
            ) : (
              <>
                <FiSave /> {t('save')}
              </>
            )}
          </button>
        </div>
      </form>

      <style jsx>{`
        .form-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 2rem;
        }

        .form-header {
          margin-bottom: 2rem;
        }

        .form-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-header p {
          color: #666;
          font-size: 1rem;
        }

        .form-header p strong {
          color: #667eea;
          background: #f0f4ff;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-family: monospace;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          margin-top: 1rem;
        }

        .spinner-large {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(102, 126, 234, 0.3);
          border-radius: 50%;
          border-top-color: #667eea;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        .loading-container p {
          color: #666;
          font-size: 1rem;
        }

        .form-section {
          margin-bottom: 2.5rem;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .form-section h3 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.2rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f0f0f0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .mobile-numbers-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .mobile-number-group {
          border-bottom: 1px solid #eee;
          padding-bottom: 1.5rem;
        }

        .mobile-number-group:last-child {
          border-bottom: none;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: 500;
          color: #495057;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .loading-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .phone-input-wrapper {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .country-code-select {
          width: 140px;
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
          cursor: pointer;
        }

        .country-code-select:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .btn-remove-number {
          padding: 8px 12px;
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .btn-remove-number:hover:not(:disabled) {
          background-color: #c82333;
        }

        .btn-add-number {
          padding: 10px 16px;
          background-color: #28a745;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          margin-top: 10px;
          width: fit-content;
        }

        .btn-add-number:hover:not(:disabled) {
          background-color: #218838;
        }

        .form-group input,
        .form-group select {
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
        }

        .form-group input[readonly] {
          background: #e9ecef;
          color: #6c757d;
          cursor: not-allowed;
        }

        .form-group select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23333' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          background-size: 16px;
          padding-right: 2.5rem;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .form-group input.error,
        .form-group select.error {
          border-color: #dc3545;
          background: #fff5f5;
        }

        .selected-info {
          margin-top: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: #f0f9ff;
          border-radius: 6px;
          border-left: 3px solid #1976d2;
          font-size: 0.9rem;
          color: #495057;
        }

        .selected-info strong {
          color: #1976d2;
        }

        .error-text {
          color: #dc3545;
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }

        .hint {
          color: #6c757d;
          font-size: 0.85rem;
          margin-top: 0.25rem;
          font-style: italic;
        }

        .checkbox-group {
          margin-top: 1rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          user-select: none;
        }

        .checkbox-label input[type="checkbox"] {
          display: none;
        }

        .checkbox-custom {
          width: 20px;
          height: 20px;
          border: 2px solid #dee2e6;
          border-radius: 4px;
          position: relative;
          transition: all 0.2s;
        }

        .checkbox-label input[type="checkbox"]:checked + .checkbox-custom {
          background: #4CAF50;
          border-color: #4CAF50;
        }

        .checkbox-label input[type="checkbox"]:checked + .checkbox-custom::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 0.8rem;
          font-weight: bold;
        }

        .checkbox-text {
          font-size: 1rem;
          color: #333;
          font-weight: 500;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding-top: 2rem;
          margin-top: 2rem;
          border-top: 1px solid #dee2e6;
        }

        .btn-secondary, .btn-primary {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          border: none;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #5a6268;
        }

        .btn-primary {
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black;
        }

        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .btn-secondary:disabled, .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner-tiny {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(102, 126, 234, 0.3);
          border-radius: 50%;
          border-top-color: #667eea;
          animation: spin 0.8s linear infinite;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .alert {
          padding: 1rem 1.5rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .alert-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert-error svg {
          flex-shrink: 0;
        }

        .alert-error span {
          flex: 1;
        }

        .btn-retry {
          background: #dc3545;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: background 0.2s;
          flex-shrink: 0;
        }

        .btn-retry:hover:not(:disabled) {
          background: #c82333;
        }

        .btn-retry:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-container {
            padding: 1rem;
          }

          .form-section {
            padding: 1rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .phone-input-wrapper {
            flex-direction: column;
          }

          .country-code-select {
            width: 100%;
          }

          .btn-add-number {
            width: 100%;
            justify-content: center;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-secondary, .btn-primary {
            width: 100%;
            justify-content: center;
          }

          .alert {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }

          .btn-retry {
            align-self: center;
            margin-top: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default EditOperator;